import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import Form from "src/components/base/form";
import { useForm, useStore } from "@tanstack/react-form";
import React from "react";
import { notify } from "src/components/base/toast";
import { Description, ErrorMessage, Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Divider } from "src/components/base/divider";
import { Button, PrimaryButton } from "src/components/base/button";
import { ApiInternal } from "src/api/api_internal";
import { Switch, SwitchField } from "src/components/base/switch";
import BlockUnsavedChanges from "src/components/base/unsaved-changes";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { Textarea } from "src/components/base/textarea";
import { Listbox, ListboxOption, ListboxLabel, ListboxDescription } from "src/components/base/listbox";
import { CopyButton } from "src/components/base/copy-button";
import { OidcClaimRemoteId } from "src/api/generated/frontend_internal_v1";
import { Text } from "src/components/base/text";
import {
    ArrowDownTrayIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ClipboardDocumentListIcon,
} from "@heroicons/react/20/solid";
import { toast } from "react-toastify/unstyled";

/**
 * The properties for {@link SettingOidc}
 */
export type SettingOidcProps = {};

/**
 * Setting page to configure SCIM
 */
function SettingOidc(props: SettingOidcProps) {
    const [t] = useTranslation("admin-settings");
    const [tg] = useTranslation();

    const router = useRouter();
    const data = Route.useLoaderData();
    const { tenantId } = Route.useParams();

    const [openCert, setOpenCert] = React.useState(false);
    const menuVariants: Variants = {
        collapsed: { height: 0, opacity: 0, overflow: "hidden" },
        expanded: { height: "auto", opacity: 1, transition: { duration: 0.3, ease: "easeInOut" } },
    };

    const MOCK_SECRET = "*".repeat(30);

    const form = useForm({
        defaultValues: {
            enabled: data.enabled,
            issuer_url: data.url,
            client_id: data.client_id,
            client_secret: data.client_secret ? MOCK_SECRET : "",
            redirect_url: data.redirect_url,
            remote_id_claim: data.remote_id,
            use_cert: data.use_cert,
        },
        onSubmit: async ({ value }) => {
            const updated_secret = value.client_secret === MOCK_SECRET ? undefined : value.client_secret;
            await ApiInternal.settings.setOidc(tenantId, {
                client_id: value.client_id,
                client_secret: updated_secret,
                enabled: value.enabled,
                url: value.issuer_url,
                remote_id: value.remote_id_claim,
                use_cert: value.use_cert,
            });
            form.reset();
            router.invalidate({ sync: true });
            notify.success(t("toast.successfully-updated"));
        },
    });
    const isDirty = useStore(form.store, (store) => store.isDirty);
    const useCert = useStore(form.store, (store) => store.values.use_cert);

    /**
     * Downloads the certificate.
     */
    const downloadCert = async () => {
        const blob = new Blob([data.cert_pem], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "ztna-tailscale-cert.pem";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Form className={"flex max-w-lg flex-col gap-6"} onSubmit={form.handleSubmit}>
            {data.errors && (
                <span className={"text-base/6 text-red-600 data-disabled:opacity-50 sm:text-sm/6 dark:text-red-500"}>
                    {data.errors}
                </span>
            )}
            <Fieldset>
                <FieldGroup>
                    <SwitchField>
                        <Label>{t("label.enable-oidc")}</Label>
                        <Switch
                            color={"blue"}
                            checked={data.enabled}
                            onChange={async (enabled) => {
                                await ApiInternal.settings.setOidc(tenantId, {
                                    ...data,
                                    client_secret: null,
                                    enabled,
                                });
                                await router.invalidate();
                            }}
                        />
                    </SwitchField>

                    <Divider />

                    <form.Field name={"issuer_url"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.issuer-url")}</Label>
                                <Description>{t("description.issuer-url")}</Description>
                                <Input
                                    type={"url"}
                                    placeholder={"https://example.com"}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"client_id"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.client-id")}</Label>
                                <Description>{t("description.client-id")}</Description>
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>

                    <form.Field name={"redirect_url"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.redirect-url")}</Label>
                                <Description>{t("description.redirect-url")}</Description>
                                <div className={"flex flex-row gap-2 pt-2"}>
                                    <Input
                                        disabled={true}
                                        type={"url"}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    <CopyButton value={fieldApi.state.value} />
                                </div>
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"remote_id_claim"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.remote-id-claim")}</Label>
                                <Description>{t("description.remote-id-claim")}</Description>
                                <Listbox<OidcClaimRemoteId>
                                    value={fieldApi.state.value}
                                    onChange={fieldApi.handleChange}
                                >
                                    <ListboxOption value={"Subject"}>
                                        <ListboxLabel>{t("label.remote-id-claim-subject")}</ListboxLabel>
                                        <ListboxDescription>
                                            {t("description.remote-id-claim-subject")}
                                        </ListboxDescription>
                                    </ListboxOption>
                                    <ListboxOption value={"ObjectId"}>
                                        <ListboxLabel>{t("label.remote-id-claim-oid")}</ListboxLabel>
                                        <ListboxDescription>{t("description.remote-id-claim-oid")}</ListboxDescription>
                                    </ListboxOption>
                                    <ListboxOption value={"SecurityId"}>
                                        <ListboxLabel>{t("label.remote-id-claim-sid")}</ListboxLabel>
                                        <ListboxDescription>{t("description.remote-id-claim-sid")}</ListboxDescription>
                                    </ListboxOption>
                                </Listbox>
                            </Field>
                        )}
                    </form.Field>

                    <Divider />

                    <form.Field name={"use_cert"}>
                        {(fieldApi) => (
                            <SwitchField className={"mb-4"}>
                                <Label>{t("label.use-cert")}</Label>
                                <Description>{t("description.use-cert")}</Description>
                                <Switch
                                    color={"blue"}
                                    checked={fieldApi.state.value}
                                    onChange={(use_cert) => fieldApi.handleChange(use_cert)}
                                />
                            </SwitchField>
                        )}
                    </form.Field>

                    {useCert ? (
                        <div className={"flex flex-col gap-2"}>
                            <div className={"flex items-center justify-between"}>
                                <div
                                    className={"flex w-full items-center gap-x-2 hover:cursor-pointer"}
                                    onClick={() => setOpenCert(!openCert)}
                                >
                                    <Text>
                                        {openCert ? (
                                            <ChevronDownIcon className={"size-5"} />
                                        ) : (
                                            <ChevronRightIcon className={"size-5"} />
                                        )}
                                    </Text>
                                    <Label>{t("label.cert")}</Label>
                                </div>
                                <div className={"flex items-center gap-x-2"}>
                                    <Button
                                        plain={true}
                                        onClick={async () => {
                                            await navigator.clipboard.writeText(data.cert_pem);
                                            toast.success(tg("toast.copied-to-clipboard"));
                                        }}
                                    >
                                        <ClipboardDocumentListIcon />
                                    </Button>
                                    <Button plain={true} onClick={async () => await downloadCert()}>
                                        <ArrowDownTrayIcon />
                                    </Button>
                                </div>
                            </div>
                            <AnimatePresence initial={false}>
                                {openCert && (
                                    <motion.div
                                        initial={"collapsed"}
                                        animate={"expanded"}
                                        exit={"collapsed"}
                                        variants={menuVariants}
                                    >
                                        <Textarea rows={20} value={data.cert_pem} readOnly={true} />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ) : (
                        <form.Field name={"client_secret"}>
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.client-secret")}</Label>
                                    <Description>{t("description.client-secret")}</Description>
                                    <Input
                                        type={"password"}
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                    {fieldApi.state.meta.errors.map((err) => (
                                        <ErrorMessage>{err}</ErrorMessage>
                                    ))}
                                </Field>
                            )}
                        </form.Field>
                    )}
                </FieldGroup>
            </Fieldset>
            <div className={"flex justify-end gap-3"}>
                <Button plain={true} disabled={!isDirty} onClick={() => form.reset()}>
                    {t("button.reset")}
                </Button>
                <PrimaryButton type="submit"> {t("button.save")}</PrimaryButton>
            </div>
            <BlockUnsavedChanges condition={isDirty} />
        </Form>
    );
}

export const Route = createFileRoute("/_admin/a/tenants/$tenantId/_tenant/oidc")({
    component: SettingOidc,
    loader: async ({ params }) => await ApiInternal.settings.getOidc(params.tenantId),
});
